import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar, RefreshCw, FileSpreadsheet, FileCode2, Building2, ShieldCheck } from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { downloadTallyXml } from "@/lib/tallyXml";
import PurgeDataButton from "@/components/PurgeDataButton";

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `₹ ${Number(n || 0).toFixed(2)}`;

export default function SuperadminVault() {
  const { user } = useAuth();
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [branchFilter, setBranchFilter] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom, date_to: dateTo, limit: 1000,
      };
      if (branchFilter && branchFilter !== "ALL") params.branch_code = branchFilter;
      const { data } = await api.get("/direct-bills/vault", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err, "Vault load failed"));
    } finally { setLoading(false); }
  }, [branchFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const byBranch = useMemo(() => {
    const groups = {};
    rows.forEach((b) => {
      const k = b.branch_code;
      if (!groups[k]) groups[k] = { count: 0, total: 0, tallyCount: 0, tallyTotal: 0 };
      groups[k].count += 1;
      groups[k].total += Number(b.total || 0);
      if (b.send_to_tally) { groups[k].tallyCount += 1; groups[k].tallyTotal += Number(b.total || 0); }
    });
    return groups;
  }, [rows]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, b) => s + Number(b.total || 0), 0);
    const tallyTotal = rows.filter(b => b.send_to_tally).reduce((s, b) => s + Number(b.total || 0), 0);
    const tallyCount = rows.filter(b => b.send_to_tally).length;
    return { count: rows.length, total, tallyTotal, tallyCount };
  }, [rows]);

  const exportExcel = () => {
    if (rows.length === 0) { toast.error("No bills to export"); return; }
    const summary = rows.map((b) => ({
      "Branch": b.branch_code,
      "Bill No": b.bill_no,
      "Date": new Date(b.generated_at).toLocaleString(),
      "Customer": b.customer_name,
      "UHID": b.mrn || "",
      "Phone": b.customer_phone || "",
      "GSTIN": b.customer_gstin || "",
      "Subtotal": Number(b.subtotal || 0),
      "CGST": Number(b.cgst_total || 0),
      "SGST": Number(b.sgst_total || 0),
      "Discount": Number(b.discount || 0),
      "Total": Number(b.total || 0),
      "Paid": Number(b.paid ?? b.total ?? 0),
      "Payment Mode": b.payment_mode || "cash",
      "Send to Tally": b.send_to_tally ? "YES" : "",
      "Verified": b.verified ? "YES" : "",
      "Archived": b.archived ? "YES" : "",
      "Cashier": b.generated_by_name || b.generated_by || "",
      "Notes": b.notes || "",
    }));
    const lines = [];
    rows.forEach((b) => (b.items || []).forEach((it) => lines.push({
      "Branch": b.branch_code,
      "Bill No": b.bill_no,
      "Date": new Date(b.generated_at).toLocaleString(),
      "Item": it.name,
      "SKU": it.sku || "",
      "HSN": it.hsn || "",
      "Qty": it.qty,
      "Rate": Number(it.price || 0),
      "GST %": Number(it.gst_pct || 0),
      "CGST": Number(it.cgst || 0),
      "SGST": Number(it.sgst || 0),
      "Amount": Number(it.line_total ?? (Number(it.qty || 0) * Number(it.price || 0))),
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Bills");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lines), "Line items");
    const branchTag = branchFilter === "ALL" ? "AllBranches" : branchFilter;
    const fname = `Vault_${branchTag}_${dateFrom}_to_${dateTo}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Vault Excel · ${summary.length} bills · ${fname}`);
  };

  const exportTallyXml = () => {
    const tally = rows.filter((b) => b.send_to_tally);
    if (tally.length === 0) { toast.error("No Tally-marked bills in the selected range"); return; }
    const branchTag = branchFilter === "ALL" ? "AllBranches" : branchFilter;
    downloadTallyXml(tally, `Tally_${branchTag}_${dateFrom}_to_${dateTo}.xml`, { company: "Badarinath Ayurveda Hospital" });
    toast.success(`Tally XML · ${tally.length} voucher${tally.length === 1 ? "" : "s"} downloaded`);
  };

  if (user?.role !== "superadmin") {
    return <div className="p-8 text-sm text-[#5C5652]">Superadmin only.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="vault-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Superadmin · Vault
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1 inline-flex items-center gap-2">
            <ShieldCheck className="h-7 w-7" strokeWidth={1.5} /> Cross-branch bill vault
          </h1>
          <p className="text-[#5C5652]">Search any past day across every branch and download consolidated Excel or a Tally-XML voucher pack.</p>
        </div>
        <PurgeDataButton collection="direct_bills_vault" label="vault bills" onDone={load} />
      </div>

      <div className="bg-white border border-sand rounded-lg p-4 mb-5 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto] items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Branch
          </label>
          <select data-testid="vault-branch" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm bg-white">
            <option value="ALL">All branches</option>
            {branches.map((b) => <option key={b.code} value={b.code}>{b.code} · {b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} /> From
          </label>
          <input type="date" data-testid="vault-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} /> To
          </label>
          <input type="date" data-testid="vault-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm" />
        </div>
        <button onClick={load} data-testid="vault-refresh" className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Reload
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-4 mb-5">
        <SummaryCard label="Bills" value={summary.count} />
        <SummaryCard label="Total collected" value={money(summary.total)} />
        <SummaryCard label={`Tally-marked (${summary.tallyCount})`} value={money(summary.tallyTotal)} accent />
        <SummaryCard label="Branches present" value={Object.keys(byBranch).length} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1"></div>
        <button onClick={exportExcel} data-testid="vault-export-xlsx"
          className="h-10 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} /> Download Excel
        </button>
        <button onClick={exportTallyXml} data-testid="vault-export-tally-xml"
          className="h-10 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2">
          <FileCode2 className="h-4 w-4" strokeWidth={1.75} /> Download Tally XML
        </button>
      </div>

      {/* Per-branch summary rollup */}
      <div className="bg-white border border-sand rounded-lg overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-sand text-[10px] uppercase tracking-wider text-[#5C5652] font-bold">Per-branch rollup</div>
        <table className="w-full text-sm" data-testid="vault-branch-rollup">
          <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
            <tr>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">Bills</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Tally-marked</th>
              <th className="text-right px-3 py-2">Tally total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8E3D9]">
            {Object.entries(byBranch).length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-[#5C5652]">No bills for the selected range.</td></tr>
            ) : Object.entries(byBranch).sort((a,b)=>a[0].localeCompare(b[0])).map(([code, s]) => (
              <tr key={code} data-testid={`vault-rollup-${code}`}>
                <td className="px-3 py-2 font-mono text-xs text-forest">{code}</td>
                <td className="px-3 py-2 text-right font-mono">{s.count}</td>
                <td className="px-3 py-2 text-right font-mono">{money(s.total)}</td>
                <td className="px-3 py-2 text-right font-mono">{s.tallyCount}</td>
                <td className="px-3 py-2 text-right font-mono text-forest">{money(s.tallyTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detailed bill list */}
      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-sand text-[10px] uppercase tracking-wider text-[#5C5652] font-bold">Detailed bills · {rows.length}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vault-bill-table">
            <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
              <tr>
                <th className="text-left px-3 py-2">Branch</th>
                <th className="text-left px-3 py-2">Bill no</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-right px-3 py-2">Tax</th>
                <th className="text-center px-3 py-2">Tally</th>
                <th className="text-center px-3 py-2">Verified</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E3D9]">
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C5652]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C5652]">No bills for the selected filters.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.id} data-testid={`vault-row-${b.branch_code}-${b.bill_no}`}>
                  <td className="px-3 py-2 font-mono text-xs text-forest">{b.branch_code}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.bill_no}</td>
                  <td className="px-3 py-2 text-xs">{new Date(b.generated_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{b.customer_name}<br/><span className="text-[10px] text-[#5C5652]">{b.mrn || b.customer_phone || "—"}</span></td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{money(b.total)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{money(b.tax_total || 0)}</td>
                  <td className="px-3 py-2 text-center text-[10px] font-bold tracking-wider">
                    {b.send_to_tally ? <span className="text-forest">YES</span> : <span className="text-[#5C5652]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-[10px] font-bold tracking-wider">
                    {b.verified ? <span className="text-forest">YES</span> : <span className="text-[#5C5652]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-[#5C5652]">
        Vault includes archived bills across all branches. Tally XML voucher packs are ready to drag into Tally &rarr; Gateway &rarr; Import &rarr; Vouchers.
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-sand rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${accent ? "text-forest" : "text-[#2A2624]"}`}>{value}</div>
    </div>
  );
}
